import React, { useEffect, useState } from 'react'
import { 
  BellIcon, 
  CheckIcon,
  TrashIcon,
  FunnelIcon,
  EyeIcon
} from '@heroicons/react/24/outline'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { Notification } from '../../types'

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'ALL' | 'UNREAD' | 'READ'>('ALL')

  useEffect(() => {
    fetchNotifications()
  }, [])

  const fetchNotifications = async () => {
    try {
      setLoading(true)
      // Simulate API call - replace with actual API call
      setTimeout(() => {
        setNotifications([
          // {
          //   id: 1,
          //   title: 'RELIANCE Target Achieved',
          //   message: 'Your RELIANCE position has reached the first target of ₹2600. Consider booking partial profits.',
          //   notification_type: 'TARGET_ACHIEVED',
          //   is_read: false,
          //   created_at: '2024-01-16T09:30:00Z'
          // },
          // {
          //   id: 2,
          //   title: 'New Premium Recommendation',
          //   message: 'A new premium recommendation for TCS has been published. Expected returns: 15-20%.',
          //   notification_type: 'NEW_RECOMMENDATION',
          //   is_read: false,
          //   created_at: '2024-01-16T11:45:00Z'
          // },
          // {
          //   id: 3,
          //   title: 'Stop Loss Alert',
          //   message: 'HDFC Bank is approaching your stop loss level of ₹1580. Current price: ₹1585.',
          //   notification_type: 'STOP_LOSS_ALERT',
          //   is_read: true,
          //   created_at: '2024-01-15T14:20:00Z',
          //   read_at: '2024-01-15T15:30:00Z'
          // },
          // {
          //   id: 4,
          //   title: 'Market Analysis Update',
          //   message: 'Weekly market analysis for the week of January 15-19, 2024 is now available.',
          //   notification_type: 'MARKET_UPDATE',
          //   is_read: true,
          //   created_at: '2024-01-15T10:00:00Z',
          //   read_at: '2024-01-15T10:30:00Z'
          // },
          // {
          //   id: 5,
          //   title: 'Subscription Renewal',
          //   message: 'Your premium subscription will expire in 7 days. Renew now to continue receiving premium recommendations.',
          //   notification_type: 'SUBSCRIPTION_REMINDER',
          //   is_read: false,
          //   created_at: '2024-01-14T08:00:00Z'
          // },
          // {
          //   id: 6,
          //   title: 'Welcome to AnalyzerView',
          //   message: 'Thank you for subscribing to our premium service. Start exploring our latest recommendations.',
          //   notification_type: 'WELCOME',
          //   is_read: true,
          //   created_at: '2024-01-10T12:00:00Z',
          //   read_at: '2024-01-10T12:05:00Z'
          // }
        ])
        setLoading(false)
      }, 1000)
    } catch (error) {
      console.error('Error fetching notifications:', error)
      setLoading(false)
    }
  }

  const markAsRead = (id: number) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, is_read: true, read_at: new Date().toISOString() }
          : notification
      )
    )
  }

  const markAllAsRead = () => {
    setNotifications(prev => 
      prev.map(notification => ({
        ...notification,
        is_read: true,
        read_at: notification.read_at || new Date().toISOString()
      }))
    )
  }

  const deleteNotification = (id: number) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id))
  }

  const filteredNotifications = notifications.filter(notification => {
    switch (filter) {
      case 'UNREAD':
        return !notification.is_read
      case 'READ':
        return notification.is_read
      default:
        return true
    }
  })

  const unreadCount = notifications.filter(n => !n.is_read).length

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'TARGET_ACHIEVED':
        return '🎯'
      case 'NEW_RECOMMENDATION':
        return '📈'
      case 'STOP_LOSS_ALERT':
        return '⚠️'
      case 'MARKET_UPDATE':
        return '📊'
      case 'SUBSCRIPTION_REMINDER':
        return '💳'
      case 'WELCOME':
        return '👋'
      default:
        return '🔔'
    }
  }

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'TARGET_ACHIEVED':
        return 'bg-green-100 text-green-800'
      case 'NEW_RECOMMENDATION':
        return 'bg-blue-100 text-blue-800'
      return 'bg-dark-700 text-gray-300'
      case 'STOP_LOSS_ALERT':
        return 'bg-dark-700 text-gray-300'
      case 'MARKET_UPDATE':
        return 'bg-purple-100 text-purple-800'
      case 'SUBSCRIPTION_REMINDER':
        return 'bg-yellow-100 text-yellow-800'
      case 'WELCOME':
        return 'bg-dark-700 text-gray-300'
      default:
        return 'bg-dark-700 text-gray-300'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
                    <h1 className="text-2xl font-bold text-gray-100 flex items-center">
            <BellIcon className="w-8 h-8 mr-3" />
            Notifications
          </h1>
          <p className="text-gray-300">Stay updated with your investment alerts and platform updates</p>
        </div>
        
        {unreadCount > 0 && (
          <div className="mt-4 sm:mt-0">
            <Button onClick={markAllAsRead} size="sm">
              <CheckIcon className="w-4 h-4 mr-1" />
              Mark All Read
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card>
        <div className="flex items-center space-x-4">
          <FunnelIcon className="w-5 h-5 text-gray-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="px-3 py-2 bg-dark-800 border border-dark-600 text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="ALL">All Notifications</option>
            <option value="UNREAD">Unread ({notifications.filter(n => !n.is_read).length})</option>
            <option value="READ">Read ({notifications.filter(n => n.is_read).length})</option>
          </select>
        </div>
      </Card>

      {/* Notifications List */}
      <div className="space-y-4">
        {filteredNotifications.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <BellIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-100 mb-2">No notifications found</h3>
              <p className="text-gray-300">
                {filter === 'UNREAD' 
                  ? "You're all caught up! No unread notifications." 
                  : "No notifications match the selected filter."}
              </p>
            </div>
          </Card>
        ) : (
          filteredNotifications.map((notification) => (
            <Card 
              key={notification.id} 
              className={`transition-all hover:shadow-md ${
                !notification.is_read ? 'border-blue-200 bg-blue-50' : ''
              }`}
            >
              <div className="flex items-start space-x-4">
                {/* Icon */}
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-dark-800 flex items-center justify-center text-lg">
                    {getNotificationIcon(notification.notification_type)}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className={`text-sm font-medium ${
                          !notification.is_read ? 'text-gray-100' : 'text-gray-300'
                        }`}>
                          {notification.title}
                        </h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          getNotificationColor(notification.notification_type)
                        }`}>
                          {notification.notification_type.replace('_', ' ')}
                        </span>
                        {!notification.is_read && (
                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        )}
                      </div>
                      
                      <p className="text-sm text-gray-300 mb-2">
                        {notification.message}
                      </p>
                      
                      <div className="flex items-center text-xs text-gray-400">
                        <span>
                          {new Date(notification.created_at).toLocaleString()}
                        </span>
                        {notification.read_at && (
                          <span className="ml-4">
                            Read: {new Date(notification.read_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      {!notification.is_read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markAsRead(notification.id)}
                        >
                          <EyeIcon className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteNotification(notification.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  )
}

export default NotificationsPage
